import React, { useMemo, useState } from "react";
import { Plus, Minus, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const ProductCard = ({ product }) => {
  const { addItem, autoPromotions } = useCart();
  const [open, setOpen] = useState(false);

  const hasAddons = (product.addon_groups || []).length > 0;

  // Detect promo applicable to this product (auto-applied only for badge display)
  const productPromo = useMemo(() => {
    if (!autoPromotions) return null;
    for (const p of autoPromotions) {
      if (p.type === "global_percent") return { label: `-${Number(p.value)}%`, kind: "global" };
      const pids = p.product_ids || [];
      if (!pids.includes(product.id)) continue;
      if (p.type === "product_percent") return { label: `-${Number(p.value)}%`, kind: "percent" };
      if (p.type === "product_fixed_price") return { label: `${Number(p.value).toFixed(2)} CHF`, kind: "fixed" };
      if (p.type === "product_amount_off") return { label: `-${Number(p.value).toFixed(2)} CHF`, kind: "amount" };
      if (p.type === "bogo") return { label: "1+1 offert", kind: "bogo" };
    }
    return null;
  }, [autoPromotions, product.id]);

  return (
    <>
      <div
        data-testid={`product-card-${product.id}`}
        onClick={() => product.available && setOpen(true)}
        className="group flex bg-white border border-neutral-200 hover:border-neutral-300 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
      >
        <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between min-w-0">
          <div>
            <h3
              className="font-bold text-base text-neutral-900 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {product.name}
            </h3>
            {product.description && (
              <p className="text-xs sm:text-sm text-neutral-500 mt-1.5 line-clamp-2">
                {product.description}
              </p>
            )}
            {hasAddons && (
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#D90429] mt-1.5">
                Personnalisable
              </p>
            )}
            {productPromo && (
              <span
                data-testid={`product-promo-badge-${product.id}`}
                className="inline-block mt-1.5 text-[10px] uppercase tracking-wider font-black bg-[#2A9D8F] text-white px-1.5 py-0.5 rounded"
              >
                Promo · {productPromo.label}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span
              className="font-bold text-base text-neutral-900"
              data-testid={`product-price-${product.id}`}
            >
              {product.price.toFixed(2)} CHF
            </span>
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (product.available) setOpen(true);
              }}
              disabled={!product.available}
              className="w-9 h-9 rounded-full bg-[#D90429] hover:bg-[#BF0424] text-white flex items-center justify-center transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed shadow-sm"
              aria-label={`Ajouter ${product.name}`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        {product.image_url && (
          <div className="w-28 sm:w-36 h-auto flex-shrink-0 overflow-hidden bg-neutral-100">
            <img
              src={product.image_url}
              alt={product.name}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src =
                  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><rect width='200' height='200' fill='%23f5f5f4'/><text x='50%25' y='50%25' font-family='sans-serif' font-size='14' fill='%23a8a29e' text-anchor='middle' dominant-baseline='middle'>Quang</text></svg>";
              }}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
      </div>

      {open && (
        <ProductDialog
          product={product}
          onClose={() => setOpen(false)}
          onAdd={(payload) => {
            addItem(product, payload);
            setOpen(false);
          }}
        />
      )}
    </>
  );
};

// --- Modal -----------------------------------------------------------
const ProductDialog = ({ product, onClose, onAdd }) => {
  const groups = product.addon_groups || [];
  // selected: { [group_id]: [option_id, ...] }
  const [selected, setSelected] = useState(() => {
    const init = {};
    groups.forEach((g) => (init[g.id] = []));
    return init;
  });
  const [comment, setComment] = useState("");
  const [qty, setQty] = useState(1);

  const toggle = (g, opt) => {
    setSelected((prev) => {
      const cur = prev[g.id] || [];
      const has = cur.includes(opt.id);
      const isRadio = !g.multi_select;
      let next;
      if (isRadio) {
        next = has ? [] : [opt.id];
      } else {
        if (has) {
          next = cur.filter((x) => x !== opt.id);
        } else {
          const max = Number(g.max_select || 0);
          if (max > 0 && cur.length >= max) return prev; // cap
          next = [...cur, opt.id];
        }
      }
      return { ...prev, [g.id]: next };
    });
  };

  const errors = useMemo(() => {
    const errs = [];
    groups.forEach((g) => {
      const cur = selected[g.id] || [];
      const min = Number(g.min_select || 0);
      if (g.required && cur.length < Math.max(min, 1)) {
        errs.push(`${g.name} : sélectionnez au moins ${Math.max(min, 1)} option(s)`);
      } else if (min > 0 && cur.length > 0 && cur.length < min) {
        errs.push(`${g.name} : sélectionnez au moins ${min} option(s)`);
      }
    });
    return errs;
  }, [groups, selected]);

  const flatSelected = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      (selected[g.id] || []).forEach((optId) => {
        const opt = g.options.find((o) => o.id === optId);
        if (opt) {
          out.push({
            group_id: g.id,
            group_name: g.name,
            option_id: opt.id,
            name: opt.name,
            price: Number(opt.price || 0),
          });
        }
      });
    });
    return out;
  }, [groups, selected]);

  const addonsTotal = useMemo(
    () => flatSelected.reduce((s, a) => s + a.price, 0),
    [flatSelected]
  );
  const unitPrice = Number(product.price || 0) + addonsTotal;
  const lineTotal = unitPrice * qty;

  const canAdd = errors.length === 0;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={`product-dialog-${product.id}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-0 overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] flex flex-col gap-0"
      >
        {product.image_url && (
          <div className="w-full h-28 sm:h-40 overflow-hidden bg-neutral-100 flex-shrink-0">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="px-5 sm:px-6 pt-4 pb-2 flex-shrink-0">
          <DialogHeader>
            <DialogTitle
              className="text-xl sm:text-2xl font-black tracking-tight pr-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {product.name}
            </DialogTitle>
          </DialogHeader>
          {product.description && (
            <p className="text-xs sm:text-sm text-neutral-500 mt-1">{product.description}</p>
          )}
        </div>

        <div className="px-5 sm:px-6 pb-4 flex-1 overflow-y-auto min-h-0">
          {groups.map((g) => (
            <div key={g.id} className="mb-5" data-testid={`addon-group-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-sm uppercase tracking-wider">{g.name}</h4>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                    g.required
                      ? "bg-[#D90429]/10 text-[#D90429]"
                      : "bg-neutral-100 text-neutral-500"
                  }`}
                >
                  {g.required ? `Requis · min ${Math.max(g.min_select || 1, 1)}` : "Optionnel"}
                  {g.multi_select && g.max_select ? ` · max ${g.max_select}` : ""}
                </span>
              </div>
              <div className="space-y-1.5">
                {g.options.map((opt) => {
                  const checked = (selected[g.id] || []).includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      data-testid={`addon-option-${opt.id}`}
                      className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
                        checked
                          ? "border-[#D90429] bg-[#D90429]/5"
                          : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggle(g, opt)}
                          data-testid={`addon-checkbox-${opt.id}`}
                          className="data-[state=checked]:bg-[#D90429] data-[state=checked]:border-[#D90429]"
                        />
                        <span className="text-sm font-medium">{opt.name}</span>
                      </span>
                      <span className="text-sm font-bold text-neutral-700 whitespace-nowrap">
                        {Number(opt.price) > 0 ? `+${Number(opt.price).toFixed(2)} CHF` : "Inclus"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mb-2">
            <label className="font-bold text-sm uppercase tracking-wider block mb-2">
              Note / Instructions
            </label>
            <Textarea
              data-testid={`product-comment-${product.id}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ex: Pas de coriandre, sauce à part…"
              className="min-h-[70px] border-neutral-300 focus-visible:ring-[#D90429]"
            />
          </div>

          {errors.length > 0 && (
            <div
              data-testid={`product-errors-${product.id}`}
              className="text-xs text-[#BF0424] bg-red-50 border border-red-200 rounded-md px-3 py-2 space-y-0.5"
            >
              {errors.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 px-5 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 bg-neutral-50 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              data-testid={`product-qty-dec-${product.id}`}
              className="w-9 h-9 rounded-full border border-neutral-300 bg-white flex items-center justify-center hover:bg-neutral-100"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span
              data-testid={`product-qty-${product.id}`}
              className="w-7 text-center font-bold text-base"
            >
              {qty}
            </span>
            <button
              onClick={() => setQty((q) => q + 1)}
              data-testid={`product-qty-inc-${product.id}`}
              className="w-9 h-9 rounded-full border border-neutral-300 bg-white flex items-center justify-center hover:bg-neutral-100"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <Button
            data-testid={`product-add-confirm-${product.id}`}
            disabled={!canAdd}
            onClick={() =>
              onAdd({ selected_addons: flatSelected, comment, quantity: qty })
            }
            className="flex-1 bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 font-bold tracking-wide disabled:opacity-40"
          >
            Ajouter · {lineTotal.toFixed(2)} CHF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductCard;
